import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
	ManualBlock,
	ManualGradeCode,
	ManualItem,
	ManualTree as ManualTreeType,
} from "@/api/adapters/patients";
import { useInterventionPanelStore } from "@/app/store/intervention-panel.store";
import { resolveManualPreselection } from "@/features/patients/manual-preselect";
import { useManualSearch, useManualTree } from "@/shared/hooks/use-manuals";
import { useProResponses } from "@/shared/hooks/use-pro-data";

const GRADE_CODES: ManualGradeCode[] = ["G1", "G2", "G3"];

export function ManualPane() {
	const { t } = useTranslation(["medical", "common"]);
	const [query, setQuery] = useState("");
	const [detail, setDetail] = useState<ManualItem | null>(null);
	const [grade, setGrade] = useState<ManualGradeCode>("G1");

	const patient = useInterventionPanelStore((s) => s.patient);
	const { data: tree = [] } = useManualTree();
	const { data: grid } = useProResponses(patient?.uid);
	const results = useManualSearch(query);

	// Open on the manual the clinician most likely needs: the 항목 this patient is doing worst on, at this
	// patient's own grade. It runs once the tree and the patient's PRO grid are both in — and only while the
	// clinician has not started browsing, so it can never yank the pane away from something they chose.
	useEffect(() => {
		if (detail || query) return;
		const preselected = resolveManualPreselection(tree, grid);
		if (!preselected) return;
		setDetail(preselected.item);
		setGrade(preselected.grade);
		// `detail`/`query` are read as guards, not tracked: re-running because the clinician opened an item is
		// exactly what must not happen.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tree, grid]);

	const openItem = (item: ManualItem) => {
		setDetail(item);
		// Land on a grade that actually has content rather than an empty 단계 1 tab.
		setGrade(item.grades[0]?.grade ?? "G1");
	};

	const handleBack = () => {
		setDetail(null);
		setQuery("");
	};

	// SHCT-785 — the search box belongs to the PANE, not to the list. It used to live inside the list
	// branch below, so opening an item returned early past it and the clinician lost the ability to
	// search while reading a manual. In the prototype the input sits outside the list container and its
	// `input` handler re-renders the list, which is what typing does here too: it drops the open detail
	// and shows the filtered tree again. [목록] clears the query, as it does there.
	const searchBox = (
		<div className="ivp-manual__search">
			<Search size={16} />
			<input
				type="text"
				maxLength={50}
				placeholder={t("patients.intervention.panel.manualSearch")}
				value={query}
				onChange={(e) => {
					setQuery(e.target.value);
					setDetail(null);
				}}
			/>
		</div>
	);

	if (detail) {
		const body = detail.grades.find((g) => g.grade === grade);
		return (
			<>
			{searchBox}
			<div className="ivp-manual__body">
				<button
					type="button"
					className="btn btn--secondary btn--sm"
					onClick={handleBack}
				>
					{t("patients.intervention.panel.manualList")}
				</button>
				<div
					style={{
						marginTop: 12,
						padding: 16,
						background: "#fff",
						border: "1px solid var(--border,#e5e7eb)",
						borderRadius: 8,
					}}
				>
					<h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700 }}>
						{body?.title ?? detail.title}
					</h3>
					<div className="manual-tree__breadcrumb">
						<span>{detail.itemName}</span>
						<span className="manual-tree__sep">›</span>
						<span>{body?.cid ?? detail.cid}</span>
					</div>
					<div
						className="manual-grade-tabs"
						role="tablist"
						style={{ marginTop: 12 }}
					>
						{GRADE_CODES.map((g) => {
							const label = t(`patients.intervention.panel.manualGrade${g}`);
							return (
								<button
									key={g}
									type="button"
									role="tab"
									aria-selected={grade === g}
									title={label}
									className={`manual-grade-tab manual-grade-tab--${g.toLowerCase()}${grade === g ? " manual-grade-tab--active" : ""}`}
									onClick={() => setGrade(g)}
								>
									{/* SHCT-866 — the tab carries the Grade label and nothing else. It used to
									    append "(미게시)" for a grade the admin has not published, which is the
									    same fact the body already states ("해당 등급의 매뉴얼이 아직 준비되지
									    않았습니다.", the wording the prototype uses) and which no PRD asks for.
									    The suffix took the row from 310px to 427px inside a ~318px pane, so it
									    was what forced a tab onto a second line. The prototype renders the
									    three labels unconditionally for the same reason. */}
									{label}
								</button>
							);
						})}
					</div>
					{body && body.blocks && body.blocks.length > 0 ? (
						// Render the authored blocks so image/video show. The sanitized `content` HTML strips
						// <img>/<iframe> at the write point, so it cannot carry media — the blocks can (image URL,
						// video URL). Text-block bodies are HTML the admin editor sanitized on write.
						//
						// SHCT-718 — the explicit `key` matters. This branch and the `content` fallback below are
						// both an unkeyed `<div className="manual-grade-body">` in the same slot, so switching
						// patients could reconcile one into the other: React kept the DOM node and set `innerHTML`
						// on it, silently detaching children it still held fibers for. The next unmount then called
						// removeChild on a node that was no longer a child → "NotFoundError: Failed to execute
						// 'removeChild' on 'Node'", and with no ErrorBoundary the whole page went blank. Distinct
						// keys force a remount instead of reuse.
						<div key="blocks" className="manual-grade-body" style={{ marginTop: 8 }}>
							{body.blocks.map((block, i) => (
								// Keyed by type as well as index: a block that changes type at the same index
								// (image → text) must remount, for the same innerHTML-over-React-children reason.
								<ManualBlockView key={`${block.type}-${i}`} block={block} />
							))}
						</div>
					) : body && body.content ? (
						<div
							key="content-html"
							className="manual-grade-body"
							style={{ marginTop: 8 }}
							// Fallback for older payloads with no block array. The body is HTML the admin editor
							// produced and sanitized on write; reference content authored by staff, never user input.
							dangerouslySetInnerHTML={{ __html: body.content }}
						/>
					) : (
						<p
							style={{ marginTop: 8, fontSize: 13, color: "#6b7280" }}
						>
							{t("patients.intervention.panel.manualNoGrade")}
						</p>
					)}
				</div>
			</div>
			</>
		);
	}

	// A search narrows the SAME tree; it never flattens the pane into a list.
	const shown = results ?? tree;
	const empty = shown.length === 0;

	return (
		<>
			{searchBox}
			<div className="ivp-manual__body">
				{empty ? (
					<div className="empty-state" style={{ padding: 24 }}>
						<p style={{ fontSize: 12 }}>
							{t("patients.intervention.panel.manualEmpty")}
						</p>
					</div>
				) : (
					<ManualTree tree={shown} onItemClick={openItem} />
				)}
			</div>
		</>
	);
}

function ManualTree({
	tree,
	onItemClick,
}: {
	tree: ManualTreeType;
	onItemClick: (item: ManualItem) => void;
}) {
	return (
		<>
			{tree.map((area) => (
				<details key={area.area} className="manual-tree__area" open>
					<summary className="manual-tree__area-head">{area.area}</summary>
					{area.subAreas.map((sa) => (
						<details key={sa.subArea} className="manual-tree__sub" open>
							<summary className="manual-tree__sub-head">{sa.subArea}</summary>
							<div className="manual-tree__items">
								{sa.items.map((item) => (
									<div
										key={item.proItemId}
										className="manual-tree__item"
										onClick={() => onItemClick(item)}
									>
										<div className="manual-tree__item-label">
											{item.itemName}
										</div>
										<div className="manual-tree__item-title">{item.title}</div>
										{item.summary && (
											<div className="manual-tree__item-summary">
												{item.summary}
											</div>
										)}
									</div>
								))}
							</div>
						</details>
					))}
				</details>
			))}
		</>
	);
}

// Map a YouTube/Vimeo watch URL to its embeddable form; '' if not a recognized https video host.
// Mirrors the admin `shared/utils/video-embed.ts` rules EXACTLY so the clinician sees the same
// embed the author previewed (ADM.044 "same rendering across all modules"). The admin module is
// the source of truth; the two apps ship separately, so this is a deliberate copy — change both
// together. SHCT-872 is what a drifted copy costs: the two used to disagree on the unlisted-video
// privacy hash, on `watch?…&v=` where `v` is not the first parameter, and on channel/group paths.
function toEmbedUrl(url: string): string {
	const trimmed = url.trim();
	if (!/^https:\/\//i.test(trimmed)) return "";

	// YouTube: youtu.be/ID, /embed/ID, watch?v=ID — `v` may sit behind other parameters (ADM.014).
	const yt =
		trimmed.match(/youtu\.be\/([\w-]{11})/) ??
		trimmed.match(/youtube\.com\/embed\/([\w-]{11})/) ??
		trimmed.match(/youtube\.com\/watch\?(?:[^#]*&)?v=([\w-]{11})/);
	if (yt) return `https://www.youtube.com/embed/${yt[1]}`;

	let parsed: URL;
	try {
		parsed = new URL(trimmed);
	} catch {
		return "";
	}
	const host = parsed.hostname.toLowerCase();
	if (host !== "vimeo.com" && !host.endsWith(".vimeo.com")) return "";

	// Vimeo: the id is the last all-numeric path segment, so `vimeo.com/ID`, `/video/ID`,
	// `/channels/<name>/ID` and `/groups/<g>/videos/ID` all embed.
	const segments = parsed.pathname.split("/").filter(Boolean);
	const unlisted = unlistedVimeoPair(segments);
	const vimeoId = unlisted
		? unlisted.id
		: segments.filter((seg) => /^\d+$/.test(seg)).pop();
	if (!vimeoId) return "";

	// SHCT-872 — an unlisted video's privacy hash must ride along as `?h=`, in the path form
	// (`vimeo.com/873265414/0894c5a3b6`) or the query form; the player refuses the bare id.
	const hash = unlisted?.hash ?? vimeoQueryHash(parsed);
	const embed = `https://player.vimeo.com/video/${vimeoId}`;
	return hash ? `${embed}?h=${hash}` : embed;
}

/**
 * The unlisted shapes `vimeo.com/<id>/<hash>` and `vimeo.com/video/<id>/<hash>`, and only those:
 * the pair must be the WHOLE path (bar a leading `video`), or `vimeo.com/channels/<numeric
 * channel>/<id>` would be misread as an id plus a hash and embed the channel, not the video.
 */
function unlistedVimeoPair(
	segments: string[],
): { id: string; hash: string } | null {
	const path =
		segments[0]?.toLowerCase() === "video" ? segments.slice(1) : segments;
	if (path.length !== 2) return null;
	const [id, hash] = path;
	if (!/^\d+$/.test(id) || !/^[0-9a-f]{6,16}$/i.test(hash)) return null;
	return { id, hash };
}

/** The `?h=` privacy hash of a player URL, '' when absent or not hash-shaped. */
function vimeoQueryHash(parsed: URL): string {
	const h = parsed.searchParams.get("h") ?? "";
	return /^[0-9a-f]{6,16}$/i.test(h) ? h : "";
}

/**
 * Render one authored manual block. Mirrors the admin block preview so the clinician sees the media
 * (image/video) that the sanitized `content` HTML cannot carry. Text-block bodies are HTML the admin
 * editor sanitized on write — reference content authored by staff, never patient/user input.
 */
function ManualBlockView({ block }: { block: ManualBlock }) {
	const title = block.title?.trim() ? (
		<strong style={{ display: "block", marginBottom: 4 }}>{block.title}</strong>
	) : null;
	const caption =
		block.showText && block.text?.trim() ? (
			<div
				style={{
					marginTop: 4,
					fontSize: 13,
					color: "#6b7280",
					whiteSpace: "pre-wrap",
				}}
			>
				{block.text}
			</div>
		) : null;

	if (block.type === "image") {
		return (
			<div style={{ marginBottom: 12 }}>
				{title}
				{block.dataUrl ? (
					<img
						src={block.dataUrl}
						alt={block.title ?? ""}
						style={{ maxWidth: "100%", borderRadius: 8, display: "block" }}
					/>
				) : null}
				{caption}
			</div>
		);
	}

	if (block.type === "video") {
		const url = block.url?.trim() ?? "";
		const embed = toEmbedUrl(url);
		return (
			<div style={{ marginBottom: 12 }}>
				{title}
				{embed ? (
					<div
						// SHCT-718 — keyed so this wrapper can never be reconciled into the text
						// block's `dangerouslySetInnerHTML` div (same tag, same slot). The outer
						// type-keyed ManualBlockView already prevents that; this keeps the
						// guarantee local if the outer key is ever changed.
						key="video-embed"
						style={{
							position: "relative",
							paddingTop: "56.25%",
							borderRadius: 8,
							overflow: "hidden",
						}}
					>
						<iframe
							src={embed}
							title={block.title?.trim() || "video"}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
							allowFullScreen
							style={{
								position: "absolute",
								inset: 0,
								width: "100%",
								height: "100%",
								border: 0,
							}}
						/>
					</div>
				) : url ? (
					<a
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						style={{ wordBreak: "break-all" }}
					>
						{url}
					</a>
				) : null}
				{caption}
			</div>
		);
	}

	// text block — BE-sanitized rich HTML.
	return (
		<div style={{ marginBottom: 12 }}>
			{title}
			{block.text?.trim() ? (
				// SHCT-718 — see the video wrapper above: distinct key, so a browser-owned
				// (innerHTML) div and a React-owned div are never the same DOM node.
				<div key="text-html" dangerouslySetInnerHTML={{ __html: block.text }} />
			) : null}
		</div>
	);
}
